/**
 * System Alerts Engine
 * Evaluates operational conditions and generates proactive alerts
 * across jobs, materials, QA, issues, contractors, procurement, and pricing.
 */

import type { Job, Issue, Contractor, ContractorOpportunity, CustomerCredit } from "./types";
import type { Quote } from "./quoteTypes";
import { computeScorecard } from "@/hooks/useContractorScorecards";
import { getContractorReliability } from "./contractorReliability";
import { getStageConfig } from "./workflowEngine";
import { getInventoryAdapter } from "./inventoryAdapter";
import { runIntegrityAudit } from "./systemIntegrity";

/* ══════════════════════════════════════════════════════════
   Types
   ══════════════════════════════════════════════════════════ */

export type AlertSeverity = "info" | "warning" | "critical";

export type AlertCategory =
  | "job"
  | "qa"
  | "issue"
  | "credit"
  | "contractor"
  | "procurement"
  | "pricing"
  | "integrity";

export interface SystemAlert {
  jobNumber: ReactNode;
  id: string;
  severity: AlertSeverity;
  category: AlertCategory;
  title: string;
  description: string;
  /** Navigation target */
  actionLabel: string;
  actionLink: string;
  /** Related entity IDs */
  jobId?: string;
  contractorId?: string;
  quoteId?: string;
  /** Timestamp for ordering */
  timestamp: string;
}

/* ══════════════════════════════════════════════════════════
   Alert Rules
   ══════════════════════════════════════════════════════════ */

const DAY_MS = 86_400_000;
const ISSUE_STALE_DAYS = 3;
const HIGH_CREDIT_THRESHOLD = 500;
const HIGH_ISSUE_RATE = 0.3;

function getJobDisplayRef(job: Job): string {
  const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
  return jobNumber || job.id;
}

function isMaterialReadyForInstall(job: Job): boolean {
  const readiness = String(job.materialReadiness || "").trim().toLowerCase();
  // "ordered" and "confirmed" mean materials are actively in the pipeline —
  // not missing, just not yet physically received. Treat as non-blocking.
  return (
    readiness === "fully received" ||
    readiness === "received" ||
    readiness === "staged for job" ||
    readiness === "loaded for crew" ||
    readiness === "ordered" ||
    readiness === "confirmed"
  );
}

function hasMaterialBlocker(job: Job): boolean {
  const readiness = String(job.materialReadiness || "").trim().toLowerCase();
  return !isMaterialReadyForInstall(job) && (
    readiness === "missing items" ||
    readiness === "not ordered" ||
    readiness === "field purchase required" ||
    readiness === "partially received"
  );
}

export function generateAlerts(
  jobs: Job[],
  issues: Issue[],
  contractors: Contractor[],
  opportunities: ContractorOpportunity[],
  credits: CustomerCredit[],
  quotes: Quote[],
): SystemAlert[] {
  const alerts: SystemAlert[] = [];
  const now = new Date();
  const nowISO = now.toISOString();
  const openJobs = jobs.filter(j => (getStageConfig(j.currentStage)?.order ?? 0) < 100);

  // ── JOB ALERTS ──

  openJobs.forEach(job => {
    const jobRef = getJobDisplayRef(job);
    const jobIssues = issues.filter(i => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"));

    // Ready for scheduling
    if (
      (getStageConfig(job.currentStage)?.order ?? 0) === 50 &&
      job.materialReadiness !== "Missing Items" &&
      (!job.permitRequired || job.permitStatus === "Approved")
    ) {
      alerts.push({
        id: `alert-ready-${job.id}`,
        severity: "info",
        category: "job",
        title: "Job Ready for Scheduling",
        description: `${jobRef} — ${job.customerName} is ready to be scheduled.`,
        actionLabel: "View Job",
        actionLink: `/jobs/${job.id}`,
        jobId: job.id,
        timestamp: nowISO,
      });
    }

    // Scheduled but not ready
    if (
      job.scheduledInstallDate &&
      (hasMaterialBlocker(job) ||
        (job.permitRequired && job.permitStatus !== "Approved"))
    ) {
      alerts.push({
        id: `alert-sched-notready-${job.id}`,
        severity: "warning",
        category: "job",
        title: "Scheduled But Not Ready",
        description: `${jobRef} is scheduled for ${job.scheduledInstallDate} but has unresolved readiness issues.`,
        actionLabel: "View Job",
        actionLink: `/jobs/${job.id}`,
        jobId: job.id,
        timestamp: nowISO,
      });
    }

    // Nearing install with missing materials
    if (job.scheduledInstallDate && !isMaterialReadyForInstall(job)) {
      const installDate = new Date(job.scheduledInstallDate);
      const daysUntil = (installDate.getTime() - now.getTime()) / DAY_MS;
      if (daysUntil <= 3 && daysUntil >= 0) {
        alerts.push({
          id: `alert-material-near-${job.id}`,
          severity: "critical",
          category: "procurement",
          title: "Materials Not Staged — Install Imminent",
          description: `${jobRef} installs in ${Math.ceil(daysUntil)} day(s) but materials are "${job.materialReadiness}".`,
          actionLabel: "Check Materials",
          actionLink: `/jobs/${job.id}`,
          jobId: job.id,
          timestamp: nowISO,
        });
      }
    }

    // QA incomplete after completion stage
    if ((getStageConfig(job.currentStage)?.order ?? 0) >= 90 && !job.qaSubmitted) {
      alerts.push({
        id: `alert-qa-incomplete-${job.id}`,
        severity: "warning",
        category: "qa",
        title: "QA Checklist Incomplete",
        description: `${jobRef} has reached ${getStageConfig(job.currentStage)?.label ?? job.currentStage} but QA has not been submitted.`,
        actionLabel: "View Job",
        actionLink: `/jobs/${job.id}`,
        jobId: job.id,
        timestamp: nowISO,
      });
    }
  });

  // ── ISSUE ALERTS ──

  const openIssues = issues.filter(i => i.status === "Open" || i.status === "In Progress");

  openIssues.forEach(issue => {
    // High severity
    if (issue.severity === "Urgent") {
      alerts.push({
        id: `alert-urgent-issue-${issue.id}`,
        severity: "critical",
        category: "issue",
        title: "Urgent Issue Logged",
        description: `${issue.issueType} on ${issue.jobId} — "${issue.description.slice(0, 80)}"`,
        actionLabel: "Resolve Issue",
        actionLink: `/jobs/${issue.jobId}`,
        jobId: issue.jobId,
        timestamp: issue.dateCreated,
      });
    }

    // Stale issue
    const created = new Date(issue.dateCreated);
    const ageDays = (now.getTime() - created.getTime()) / DAY_MS;
    if (ageDays > ISSUE_STALE_DAYS && issue.status === "Open") {
      alerts.push({
        id: `alert-stale-issue-${issue.id}`,
        severity: "warning",
        category: "issue",
        title: "Issue Open Beyond Threshold",
        description: `${issue.issueType} on ${issue.jobId} has been open for ${Math.floor(ageDays)} days.`,
        actionLabel: "Resolve Issue",
        actionLink: `/jobs/${issue.jobId}`,
        jobId: issue.jobId,
        timestamp: issue.dateCreated,
      });
    }
  });

  // ── CREDIT ALERTS ──

  credits.forEach(cr => {
    if (cr.creditAmount >= HIGH_CREDIT_THRESHOLD) {
      alerts.push({
        id: `alert-high-credit-${cr.id}`,
        severity: "warning",
        category: "credit",
        title: "High Credit Issued",
        description: `$${cr.creditAmount.toLocaleString()} ${cr.creditType} on ${cr.jobId}.`,
        actionLabel: "View Job",
        actionLink: `/jobs/${cr.jobId}`,
        jobId: cr.jobId,
        timestamp: cr.dateCreated,
      });
    }
  });

  // ── CONTRACTOR RISK ALERTS ──

  contractors.filter(c => c.active).forEach(contractor => {
    const scorecard = computeScorecard(contractor, opportunities, jobs, issues, credits);
    const reliability = getContractorReliability(scorecard);

    if (reliability.tier === "Risk") {
      alerts.push({
        id: `alert-contractor-risk-${contractor.id}`,
        severity: "critical",
        category: "contractor",
        title: "Contractor At Risk",
        description: `${contractor.companyName} is flagged as "Risk" (score: ${reliability.score}).`,
        actionLabel: "Review Contractor",
        actionLink: `/contractors/${contractor.id}`,
        contractorId: contractor.id,
        timestamp: nowISO,
      });
    } else if (reliability.tier === "Watch") {
      alerts.push({
        id: `alert-contractor-watch-${contractor.id}`,
        severity: "warning",
        category: "contractor",
        title: "Contractor On Watch",
        description: `${contractor.companyName} is flagged as "Watch" — monitor performance.`,
        actionLabel: "Review Contractor",
        actionLink: `/contractors/${contractor.id}`,
        contractorId: contractor.id,
        timestamp: nowISO,
      });
    }

    // Repeated credits
    const contractorCredits = credits.filter(c => c.relatedContractorId === contractor.id);
    if (contractorCredits.length >= 3) {
      alerts.push({
        id: `alert-contractor-credits-${contractor.id}`,
        severity: "warning",
        category: "credit",
        title: "Repeated Credits — Contractor",
        description: `${contractor.companyName} has ${contractorCredits.length} credits totaling $${contractorCredits.reduce((s, c) => s + c.creditAmount, 0).toLocaleString()}.`,
        actionLabel: "Review Contractor",
        actionLink: `/contractors/${contractor.id}`,
        contractorId: contractor.id,
        timestamp: nowISO,
      });
    }
  });

  // ── PROCUREMENT ALERTS ──

  try {
    const snapshot = getInventoryAdapter().getSnapshot();
    snapshot.items.forEach(item => {
      if (item.status === "Out of Stock") {
        alerts.push({
          id: `alert-oos-${item.sku}`,
          severity: "critical",
          category: "procurement",
          title: "Out of Stock",
          description: `${item.itemName} (${item.sku}) has zero available inventory.`,
          actionLabel: "Check Materials",
          actionLink: "/inventory",
          timestamp: nowISO,
        });
      } else if (item.status === "Low Stock") {
        alerts.push({
          id: `alert-low-${item.sku}`,
          severity: "warning",
          category: "procurement",
          title: "Low Stock Alert",
          description: `${item.itemName} (${item.sku}) — ${item.quantityAvailable} available (reorder at ${item.reorderPoint}).`,
          actionLabel: "Check Materials",
          actionLink: "/inventory",
          timestamp: nowISO,
        });
      }
    });
  } catch {
    // Inventory not available
  }

  // ── PRICING ALERTS ──

  quotes.forEach(quote => {
    if (quote.pricingStatus === "Below Floor") {
      alerts.push({
        id: `alert-pricing-floor-${quote.id}`,
        severity: "critical",
        category: "pricing",
        title: "Quote Below Floor Price",
        description: `${quote.id} for ${quote.customerName} is priced below the minimum acceptable floor.`,
        actionLabel: "Review Quote",
        actionLink: `/opportunities/${quote.id}`,
        quoteId: quote.id,
        timestamp: quote.updatedAt,
      });
    }
    if (quote.pricingOverride && Math.abs(quote.pricingOverride.variance) > 15) {
      alerts.push({
        id: `alert-pricing-override-${quote.id}`,
        severity: "warning",
        category: "pricing",
        title: "Large Pricing Override",
        description: `${quote.id} has a ${quote.pricingOverride.variance.toFixed(0)}% price variance from calculated total.`,
        actionLabel: "Review Quote",
        actionLink: `/opportunities/${quote.id}`,
        quoteId: quote.id,
        timestamp: quote.updatedAt,
      });
    }
  });

  // ── INTEGRITY ALERTS (surface critical findings) ──
  try {
    const integrityReport = runIntegrityAudit(jobs, quotes, issues, credits, contractors, opportunities);
    const criticalFindings = integrityReport.findings.filter(f => f.severity === "critical");
    if (criticalFindings.length > 0) {
      alerts.push({
        id: "alert-integrity-critical",
        severity: "critical",
        category: "integrity",
        title: `${criticalFindings.length} Critical Integrity Issue${criticalFindings.length > 1 ? "s" : ""}`,
        description: `System audit found ${criticalFindings.length} critical finding(s) requiring attention.`,
        actionLabel: "View Audit",
        actionLink: "/system-audit",
        timestamp: nowISO,
      });
    }
    const warningFindings = integrityReport.findings.filter(f => f.severity === "warning");
    if (warningFindings.length > 3) {
      alerts.push({
        id: "alert-integrity-warnings",
        severity: "warning",
        category: "integrity",
        title: `${warningFindings.length} Integrity Warnings`,
        description: `System audit found ${warningFindings.length} warnings across modules.`,
        actionLabel: "View Audit",
        actionLink: "/system-audit",
        timestamp: nowISO,
      });
    }
  } catch {
    // Integrity check failed — non-blocking
  }

  // Sort: critical first, then warning, then info; within same severity by recency
  const severityOrder: Record<AlertSeverity, number> = { critical: 0, warning: 1, info: 2 };
  const sorted = alerts.sort((a, b) => {
    const sd = severityOrder[a.severity] - severityOrder[b.severity];
    if (sd !== 0) return sd;
    return b.timestamp.localeCompare(a.timestamp);
  });

  return deduplicateAlerts(sorted);
}

/**
 * Deduplicate alerts by ID, keeping only the first occurrence.
 * Also groups alerts with identical titles, keeping the most severe.
 */
function deduplicateAlerts(alerts: SystemAlert[]): SystemAlert[] {
  const seen = new Map<string, SystemAlert>();
  const titleSeen = new Map<string, SystemAlert>();
  const severityOrder: Record<AlertSeverity, number> = { critical: 0, warning: 1, info: 2 };

  for (const alert of alerts) {
    // Dedup by ID
    if (!seen.has(alert.id)) {
      seen.set(alert.id, alert);
    }

    // Group by title+entity: keep most severe
    const groupKey = `${alert.title}|${alert.jobId || ""}|${alert.contractorId || ""}`;
    const existing = titleSeen.get(groupKey);
    if (!existing || severityOrder[alert.severity] < severityOrder[existing.severity]) {
      titleSeen.set(groupKey, alert);
    }
  }

  // Return deduplicated by ID
  return Array.from(seen.values());
}

/** Filter alerts for a specific job */
export function getJobAlerts(allAlerts: SystemAlert[], jobId: string): SystemAlert[] {
  return allAlerts.filter(a => a.jobId === jobId);
}

/** Filter alerts for a specific contractor */
export function getContractorAlerts(allAlerts: SystemAlert[], contractorId: string): SystemAlert[] {
  return allAlerts.filter(a => a.contractorId === contractorId);
}

/** Get summary counts by severity */
export function getAlertCounts(alerts: SystemAlert[]): Record<AlertSeverity, number> {
  return {
    critical: alerts.filter(a => a.severity === "critical").length,
    warning: alerts.filter(a => a.severity === "warning").length,
    info: alerts.filter(a => a.severity === "info").length,
  };
}
